import React, { useState, useEffect, useRef } from 'react';
import { enrollInMfa, completeMfaEnrollment, reauthenticateUser, clearRecaptchaVerifier, sendEmailVerification, checkEmailVerified, waitForEmailVerification, auth, TEST_MODE, isMobileDevice, isChromeMobileEmulator } from '../firebase';

// Country codes and flags data
const countryCodes = [
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', name: 'France', flag: '🇫🇷' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', name: 'Mexico', flag: '🇲🇽' },
  { code: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: '+7', name: 'Russia', flag: '🇷🇺' },
  { code: '+27', name: 'South Africa', flag: '🇿🇦' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
];

// ReauthDialog component for when a fresh login is required
const ReauthDialog = ({ onSuccess, onCancel }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    console.log('🔍 DEBUG: ReauthDialog handleSubmit called');
    
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      if (!password.trim()) {
        throw new Error('Please enter your password');
      }

      console.log('🔍 DEBUG: About to call reauthenticateUser');
      
      // Clear any existing recaptcha verifiers before reauthenticating
      try {
        clearRecaptchaVerifier();
      } catch (err) {
        console.warn("Error clearing recaptcha before reauthentication:", err);
      }

      // Wait a moment to ensure cleanup
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await reauthenticateUser(password);
      
      console.log('🔍 DEBUG: reauthenticateUser completed successfully');
      
      // After successful reauthentication, wait briefly before continuing
      await new Promise(resolve => setTimeout(resolve, 300));
      
      console.log('🔍 DEBUG: About to call onSuccess');
      onSuccess();
    } catch (error) {
      console.error('🔍 DEBUG: Reauthentication error:', error);
      setError(error.message || 'Failed to verify. Please check your password and try again.');
    } finally {
      setLoading(false);
      console.log('🔍 DEBUG: ReauthDialog handleSubmit completed');
    }
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Verify Your Identity
      </h3>
      
      <p className="text-sm text-gray-700 dark:text-white/80 mb-4">
        For your security, please verify your identity before continuing.
      </p>

      <form 
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('🔍 DEBUG: ReauthDialog form onSubmit triggered');
          handleSubmit(e);
          return false;
        }}
        noValidate
      >
        <div className="mb-4">
          <label 
            htmlFor="password" 
            className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 pr-12 rounded-lg border border-gray-300 dark:border-white/20 focus:outline-none focus:ring-2 focus:ring-brand dark:bg-black/50 text-gray-900 dark:text-white"
              placeholder="Enter your password"
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              disabled={loading}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔍 DEBUG: ReauthDialog Cancel button clicked');
              // Clean up before canceling
              try {
                clearRecaptchaVerifier();
              } catch (err) {
                console.warn("Error clearing recaptcha on cancel:", err);
              }
              onCancel();
            }}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔍 DEBUG: ReauthDialog Verify button clicked');
              handleSubmit(e);
            }}
            className="px-4 py-2 rounded-lg bg-brand hover:bg-[#F9A06B] text-black font-medium transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </form>
    </div>
  );
};

// PhoneInput component for country selection and phone number input
const PhoneInput = ({ value, onChange, disabled }) => {
  const [nationalNumber, setNationalNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1'); // Default to US
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Format phone number for display only
  const formatPhoneNumberForDisplay = (input) => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');
    // Format the number with spaces or dashes
    let formatted = digits;
    
    // Apply specific formatting depending on common phone number patterns
    if (digits.length > 3 && digits.length <= 6) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else if (digits.length > 6) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
    
    return formatted;
  };

  // Parse international number from autofill
  const parseInternationalNumber = (input) => {
    // Check if the input looks like an international number (starts with +)
    if (input.startsWith('+')) {
      console.log('Detected international number from autofill:', input);
      
      // Find matching country code
      const matchingCountry = countryCodes.find(country => 
        input.startsWith(country.code)
      );
      
      if (matchingCountry) {
        const detectedCountryCode = matchingCountry.code;
        const nationalPart = input.substring(detectedCountryCode.length);
        
        console.log('Parsed autofill:', {
          original: input,
          countryCode: detectedCountryCode,
          nationalNumber: nationalPart
        });
        
        return {
          countryCode: detectedCountryCode,
          nationalNumber: nationalPart
        };
      }
    }
    
    // Check if input looks like a US number without country code (11 digits starting with 1)
    // Chrome sometimes autofills US numbers as just "14155551234" without the "+"
    const digitsOnly = input.replace(/\D/g, '');
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      console.log('Detected US 11-digit number from autofill (no +):', input);
      const nationalPart = digitsOnly.substring(1); // Remove the leading 1
      
      return {
        countryCode: '+1',
        nationalNumber: nationalPart
      };
    }
    
    // Check if input looks like a US number without country code (10 digits)
    // Chrome sometimes autofills US numbers as just "4155551234"
    if (digitsOnly.length === 10 && !input.startsWith('+')) {
      console.log('Detected US 10-digit number from autofill:', input);
      
      return {
        countryCode: '+1',
        nationalNumber: digitsOnly
      };
    }
    
    return null;
  };

  // Update the combined value whenever country code or national number changes
  useEffect(() => {
    // Store raw digits for processing (E.164 format)
    const rawDigits = nationalNumber.replace(/\D/g, '');
    if (rawDigits.length > 0) {
      let combined;
      
      // Special handling for US/Canada (+1) numbers
      if (countryCode === '+1') {
        // If user entered 11 digits starting with 1 (like 14155551234), 
        // remove the leading 1 since +1 is already the country code
        if (rawDigits.length === 11 && rawDigits.startsWith('1')) {
          combined = `+1${rawDigits.substring(1)}`;
          console.log("US 11-digit number detected, removing leading 1:", rawDigits, "→", combined);
        }
        // If user entered 10 digits (like 4155551234), use as-is
        else if (rawDigits.length === 10) {
          combined = `+1${rawDigits}`;
          console.log("US 10-digit number detected:", rawDigits, "→", combined);
        }
        // For other lengths, just combine normally
        else {
          combined = `${countryCode}${rawDigits}`;
          console.log("US number with unusual length:", rawDigits, "→", combined);
        }
      } else {
        // For non-US countries, combine normally
        combined = `${countryCode}${rawDigits}`;
        console.log("Non-US number:", rawDigits, "→", combined);
      }
      
      onChange(combined);
      console.log("Combined phone number updated:", combined);
    } else {
      // If no national number, just pass the country code
      onChange(countryCode);
      console.log("Only country code present:", countryCode);
    }
  }, [countryCode, nationalNumber, onChange]);

  // Attempt to detect user's country
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try to use the browser's language setting as a fallback
        const language = navigator.language || 'en-US';
        const country = language.split('-')[1] || 'US';
        
        // Map of country codes - simplified version
        const countryToDialCode = {
          'US': '+1',   // United States
          'CA': '+1',   // Canada
          'GB': '+44',  // United Kingdom
          'AU': '+61',  // Australia
          'DE': '+49',  // Germany
          'FR': '+33',  // France
          'IN': '+91',  // India
          'JP': '+81',  // Japan
          'CN': '+86',  // China
          'BR': '+55',  // Brazil
          'MX': '+52',  // Mexico
        };
        
        // Set the country code if we have a mapping
        if (countryToDialCode[country]) {
          setCountryCode(countryToDialCode[country]);
        }
      } catch (error) {
        console.error('Error detecting country:', error);
        // Keep default +1 if detection fails
      }
    };
    
    detectCountry();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleNationalNumberChange = (e) => {
    const inputValue = e.target.value;
    
    // Check if this looks like autofill with international number
    const parsedInternational = parseInternationalNumber(inputValue);
    
    if (parsedInternational) {
      // Handle autofill case - set both country code and national number
      setCountryCode(parsedInternational.countryCode);
      const formattedNational = formatPhoneNumberForDisplay(parsedInternational.nationalNumber);
      setNationalNumber(formattedNational);
      
      console.log('Autofill handled:', {
        countryCode: parsedInternational.countryCode,
        nationalNumber: formattedNational
      });
    } else {
      // Handle manual input case
      const formattedForDisplay = formatPhoneNumberForDisplay(inputValue);
      setNationalNumber(formattedForDisplay);
      
      // For debugging - log the full phone number
      const rawDigits = formattedForDisplay.replace(/\D/g, '');
      console.log("Manual phone number update:", `${countryCode}${rawDigits}`);
    }
  };

  const handleCountrySelect = (code) => {
    setCountryCode(code);
    setShowDropdown(false);
  };

  const getCurrentFlag = () => {
    const country = countryCodes.find(c => c.code === countryCode);
    return country ? country.flag : '🌐';
  };

  return (
    <div className="relative">
      <div className="flex flex-col sm:flex-row">
        <div className="relative mb-2 sm:mb-0">
          <button 
            type="button"
            className="w-full sm:w-auto px-3 py-2 min-h-[42px] border border-gray-300 dark:border-white/20 rounded-lg sm:rounded-r-none sm:rounded-l-lg bg-gray-50 dark:bg-black/50 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-brand"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={disabled}
            aria-label="Select country code"
          >
            <span className="mr-1">{getCurrentFlag()}</span>
            <span>{countryCode}</span>
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDropdown && (
            <div 
              ref={dropdownRef}
              className="absolute z-10 mt-1 w-full sm:w-64 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto left-0 right-0"
            >
              <div className="p-2">
                {countryCodes.map((country) => (
                  <button
                    key={`${country.code}-${country.name}`}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center"
                    onClick={() => handleCountrySelect(country.code)}
                  >
                    <span className="mr-2">{country.flag}</span>
                    <span className="font-medium">{country.name}</span>
                    <span className="ml-auto text-gray-500 dark:text-gray-400">{country.code}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <input
          ref={inputRef}
          type="tel"
          value={nationalNumber}
          onChange={handleNationalNumberChange}
          className="flex-1 px-4 py-2 min-h-[42px] border border-gray-300 dark:border-white/20 rounded-lg sm:rounded-l-none sm:rounded-r-lg focus:outline-none focus:ring-2 focus:ring-brand dark:bg-black/50 text-gray-900 dark:text-white"
          placeholder="Phone number"
          disabled={disabled}
          inputMode="tel"
          autoComplete="tel"
          aria-label="Phone number"
        />
      </div>
    </div>
  );
};

const MfaEnrollment = ({ user, onSuccess, onCancel }) => {
  // Check if email is verified and set initial step accordingly
  const initialStep = user?.emailVerified ? 1 : 0; // Step 0: Email verification, Step 1: Phone setup, Step 2: Code verification
  
  const [step, setStep] = useState(initialStep);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [displayName, setDisplayName] = useState('My Phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);
  
  // Email verification states
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [checkingEmailVerification, setCheckingEmailVerification] = useState(false);
  const [emailCheckInterval, setEmailCheckInterval] = useState(null);
  
  const formRef = useRef(null);
  const verificationInputRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  // Add a state to track third-party cookie issues
  const [cookieIssueDetected, setCookieIssueDetected] = useState(false);

  // Add state variables for mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileEmulator, setIsMobileEmulator] = useState(false);

  // Check for mobile device on component mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
    setIsMobileEmulator(isChromeMobileEmulator());
    
    if (isMobileDevice() || isChromeMobileEmulator()) {
      console.log("Mobile device or emulator detected, applying special configuration");
    }
  }, []);

  // Pre-initialize recaptcha when component mounts
  useEffect(() => {
    // Only set up reCAPTCHA if not in the reauthentication state
    if (needsReauth) return;
    
    // Create a unique recaptcha container ID
    const recaptchaContainerId = `recaptcha-container-${Math.random().toString(36).substring(2, 15)}`;
    
    // Prepare reCAPTCHA container
    const container = document.createElement('div');
    container.id = recaptchaContainerId;
    
    // For efficiency, we'll leave the actual styling to the Firebase functions
    document.body.appendChild(container);
    
    // Set the container reference
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.id = recaptchaContainerId;
    }
    
    // Load reCAPTCHA script if not already loaded
    if (!window.recaptchaScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      window.recaptchaScriptLoaded = true;
    }
    
    // Cleanup function
    return () => {
      try {
        if (container && document.body.contains(container)) {
          document.body.removeChild(container);
        }
        
        // Also clean up any possible recaptcha verifiers
        clearRecaptchaVerifier();
      } catch (error) {
        console.warn("Error removing reCAPTCHA container:", error);
      }
    };
  }, [needsReauth]); // Re-initialize when reauthentication state changes

  // Focus verification input when step changes to 2
  useEffect(() => {
    if (step === 2 && verificationInputRef.current) {
      setTimeout(() => {
        verificationInputRef.current.focus();
      }, 300);
    }
  }, [step]);

  // Check for common issues on component mount
  useEffect(() => {
    const checkForRecaptchaIssues = () => {
      // Test for third-party cookies
      try {
        const testCookieName = 'recaptcha_test_' + Date.now();
        document.cookie = `${testCookieName}=1; SameSite=None; Secure`;
        const hasCookie = document.cookie.indexOf(testCookieName) !== -1;
        if (!hasCookie) {
          console.warn("Third-party cookies appear to be blocked. reCAPTCHA may not work.");
          setCookieIssueDetected(true);
        }
      } catch (e) {
        console.warn("Error testing cookies:", e);
      }
    };
    
    checkForRecaptchaIssues();
    
    // Always clean up any lingering reCAPTCHA instances
    try {
      clearRecaptchaVerifier();
    } catch (err) {
      console.warn("Error cleaning up reCAPTCHA:", err);
    }
    
    return () => {
      // Clean up on unmount
      try {
        clearRecaptchaVerifier();
      } catch (err) {
        console.warn("Error cleaning up reCAPTCHA on unmount:", err);
      }
    };
  }, []);

  const handleSendCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    
    console.log('🔍 DEBUG: handleSendCode called', {
      loading,
      phoneNumber,
      step,
      hasPreventDefault: !!(e && e.preventDefault)
    });
    
    if (loading) {
      console.log('🔍 DEBUG: Already loading, ignoring request');
      return;
    }
    
    setLoading(true);
    setError(null);
    setRecaptchaFailed(false); // Reset recaptcha failed state
    
    try {
      // Basic validation
      if (!phoneNumber) {
        throw new Error('Please enter your phone number');
      }
      
      // Format phone number to E.164 format (remove spaces and non-digit characters)
      const formattedPhoneNumber = phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');
      
      // Log the phone number for debugging
      console.log("Formatted phone number:", formattedPhoneNumber);
      console.log("Original phone number state:", phoneNumber);

      // If cookies are blocked, show a warning but still attempt to proceed
      if (cookieIssueDetected) {
        console.warn("Attempting to use reCAPTCHA with potential cookie issues");
      }

      // Special handling for mobile devices
      if ((isMobile || isMobileEmulator) && TEST_MODE) {
        console.log("Mobile device in TEST_MODE: special handling enabled for reCAPTCHA");
      } else if (isMobile || isMobileEmulator) {
        console.log("Mobile device in PRODUCTION mode: using standard reCAPTCHA flow");
      }

      // Clean up any previous reCAPTCHA instances first
      try {
        await clearRecaptchaVerifier();
        console.log("Successfully cleared previous reCAPTCHA instances");
      } catch (err) {
        console.warn("Error cleaning up previous reCAPTCHA:", err);
      }

      // Log that we're in TEST_MODE if we are
      if (TEST_MODE) {
        console.log("Running reCAPTCHA in TEST_MODE with phone number:", formattedPhoneNumber);
      }

      // Send verification code
      try {
        console.log(`🔍 DEBUG: About to call enrollInMfa with phone number: ${formattedPhoneNumber}`);
        const verId = await enrollInMfa(user, formattedPhoneNumber);
        console.log("✅ DEBUG: Successfully obtained verification ID:", verId ? 'Yes' : 'No');
        
        if (!verId) {
          throw new Error('Failed to get verification ID. Please try again.');
        }
        
        setVerificationId(verId);
        
        // Store the formatted phone number before moving to step 2
        setPhoneNumber(formattedPhoneNumber);
        
        // Clear any existing errors before advancing
        setError(null);
        setRecaptchaFailed(false);
        
        // Advance to verification step
        console.log("✅ DEBUG: Advancing to step 2 (verification code input)");
        setStep(2);
        
        // Focus on verification input after a brief delay
        setTimeout(() => {
          if (verificationInputRef.current) {
            verificationInputRef.current.focus();
          }
        }, 100);
        
      } catch (error) {
        console.error("❌ DEBUG: Error during MFA enrollment:", error.code, error.message);
        console.error("❌ DEBUG: Full error:", error);
        
        // Check for specific reCAPTCHA errors
        if (error.code === 'auth/argument-error') {
          console.error("RecaptchaVerifier argument error:", error);
          
          if ((isMobile || isMobileEmulator) && !TEST_MODE) {
            setError(
              <>
                <strong>Security verification failed on this mobile device.</strong>
                <p className="mt-2">
                  We're having trouble with reCAPTCHA verification on your mobile device.
                </p>
                <p className="mt-2">
                  Please try the following:
                </p>
                <ul className="list-disc ml-5 mt-1 text-xs">
                  <li>Try on a desktop browser instead</li>
                  <li>Disable any ad blockers or privacy extensions</li>
                  <li>Enable third-party cookies in your browser settings</li>
                  <li>Use Chrome browser for best compatibility</li>
                </ul>
              </>
            );
            setRecaptchaFailed(true);
          } else if ((isMobile || isMobileEmulator) && TEST_MODE) {
            setError(
              <>
                <strong>Security verification failed in test mode.</strong>
                <p className="mt-2">
                  This is a known issue with mobile devices in test mode.
                </p>
                <p className="mt-2">
                  Please contact the administrator for assistance.
                </p>
              </>
            );
            setRecaptchaFailed(true);
          } else {
            setError(
              <>
                <strong>Security verification failed.</strong>
                <p className="mt-2">
                  This is usually due to browser privacy settings blocking reCAPTCHA.
                </p>
                <p className="mt-2">
                  Please try the following:
                </p>
                <ul className="list-disc ml-5 mt-1 text-xs">
                  <li>Allow third-party cookies in your browser settings</li>
                  <li>Disable any privacy extensions or ad blockers temporarily</li>
                  <li>Try using a different browser (Chrome works best)</li>
                  <li>Refresh the page and try again</li>
                </ul>
              </>
            );
            setRecaptchaFailed(true);
          }
          // Don't return early - let it fall through to stay on current step
        } else if (error.code === 'auth/requires-recent-login') {
          // For reauthentication requirements, show the reauth dialog
          // but don't change the step to prevent UI from disappearing
          console.log("❌ DEBUG: Setting needsReauth=true but preserving current step");
          setNeedsReauth(true);
        } else if (error.code === 'auth/unverified-email') {
          // Email verification required before MFA enrollment
          console.log("❌ DEBUG: Email verification required, redirecting to step 0");
          setStep(0);
          setError("Email verification is required before setting up 2FA. Please verify your email first.");
        } else if (error.code === 'auth/billing-not-enabled') {
          // Special handling for billing not enabled error
          setError(
            <>
              <strong>Phone authentication is currently unavailable.</strong>
              <p className="mt-2">
                This is because Firebase phone authentication requires billing to be fully configured.
              </p>
              <p className="mt-2">
                Please contact the administrator or try again later.
              </p>
            </>
          );
        } else if (error.code === 'auth/invalid-app-credential' || error.code === 'auth/captcha-check-failed') {
          // Special handling for reCAPTCHA verification issues
          setRecaptchaFailed(true);
          setError(
            <>
              <strong>Security verification failed.</strong>
              <p className="mt-2">
                This is usually due to domain verification issues with our security system.
              </p>
              <p className="mt-2">
                Try the following:
              </p>
              <ul className="list-disc ml-5 mt-1 text-xs">
                <li>Refresh the page and try again</li>
                <li>Try using a different browser</li>
                <li>Ensure you're not using a VPN or proxy</li>
                <li>Allow 3rd party cookies in your browser</li>
                <li>Contact support if the issue persists</li>
              </ul>
            </>
          );
        } else if (error.message && error.message.includes('reCAPTCHA')) {
          // If it's a reCAPTCHA error, show a more friendly message
          setRecaptchaFailed(true);
          setError('There was an issue with the security verification. Please try again.');
        } else {
          // For any other error, display it but stay on current step
          setError(error.message || 'Failed to send verification code. Please try again.');
        }
        
        // Don't change steps or cause navigation on error
        console.log('❌ DEBUG: Error handled, staying on current step:', step);
      }
    } catch (error) {
      console.error('❌ DEBUG: Outer catch - Error sending verification code:', error);
      if (!error.handled) {
        setError(error.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setLoading(false);
      console.log('🔍 DEBUG: handleSendCode completed, step is now:', step);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      if (!verificationCode.trim()) {
        throw new Error('Please enter the verification code');
      }

      // Ensure verification code contains only digits
      const cleanedVerificationCode = verificationCode.replace(/\D/g, '');
      
      if (cleanedVerificationCode.length !== 6) {
        throw new Error('Please enter a valid 6-digit verification code');
      }

      // Complete enrollment
      try {
        await completeMfaEnrollment(user, verificationId, cleanedVerificationCode, displayName);
        onSuccess();
      } catch (error) {
        // Check if the error is because reauthentication is required
        if (error.code === 'auth/requires-recent-login') {
          setNeedsReauth(true);
        } else if (error.message && error.message.includes('reCAPTCHA')) {
          // If it's a reCAPTCHA error, show a more friendly message
          throw new Error('There was an issue with the security verification. Please try again.');
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Format phone number to E.164 format (remove spaces and non-digit characters)
      const formattedPhoneNumber = phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');
      
      // Clean up any previous reCAPTCHA instances first
      try {
        clearRecaptchaVerifier();
      } catch (err) {
        console.warn("Error cleaning up previous reCAPTCHA:", err);
      }
        
      // Resend verification code
      try {
        const verId = await enrollInMfa(user, formattedPhoneNumber);
        setVerificationId(verId);
        setVerificationCode('');
        setError(null);
      } catch (error) {
        // Check if the error is because reauthentication is required
        if (error.code === 'auth/requires-recent-login') {
          setNeedsReauth(true);
        } else if (error.message && error.message.includes('reCAPTCHA')) {
          // If it's a reCAPTCHA error, show a more friendly message
          throw new Error('There was an issue with the security verification. Please try again.');
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error resending verification code:', error);
      setError(error.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReauthSuccess = () => {
    console.log('🔍 DEBUG: handleReauthSuccess called');
    
    // Prevent any potential form submission or navigation
    try {
      // Stop any ongoing form submissions
      if (window.event) {
        window.event.preventDefault();
        window.event.stopPropagation();
        window.event.stopImmediatePropagation();
      }
    } catch (e) {
      console.log('🔍 DEBUG: No window.event to prevent');
    }
    
    setNeedsReauth(false);
    setError(null);
    
    // Clean up any lingering reCAPTCHA instances
    try {
      clearRecaptchaVerifier();
    } catch (error) {
      console.warn("Error clearing reCAPTCHA after reauthentication:", error);
    }
    
    // Use a longer delay to ensure everything settles before retrying
    setTimeout(() => {
      console.log('🔍 DEBUG: Retrying operation after reauthentication, current step:', step);
      
      // After successful reauthentication, retry the operation
      // Create a fake event object to avoid errors
      const fakeEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        stopImmediatePropagation: () => {}
      };
      
      if (step === 1) {
        console.log('🔍 DEBUG: Retrying handleSendCode after reauthentication');
        handleSendCode(fakeEvent);
      } else if (step === 2) {
        console.log('🔍 DEBUG: Retrying handleVerifyCode after reauthentication');
        handleVerifyCode(fakeEvent);
      } else {
        console.log('🔍 DEBUG: Unexpected step after reauthentication:', step);
      }
    }, 1000); // Increased delay from 500ms to 1000ms
  };

  // Email verification functions
  const handleSendEmailVerification = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await sendEmailVerification(user);
      setEmailVerificationSent(true);
      
      // Start checking for email verification
      setCheckingEmailVerification(true);
      const interval = setInterval(async () => {
        const isVerified = await checkEmailVerified(user);
        if (isVerified) {
          clearInterval(interval);
          setEmailCheckInterval(null);
          setCheckingEmailVerification(false);
          setStep(1); // Move to phone setup step
          console.log("Email verified! Moving to phone setup step");
        }
      }, 3000); // Check every 3 seconds
      
      setEmailCheckInterval(interval);
    } catch (error) {
      console.error('Error sending email verification:', error);
      setError(error.message || 'Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmailVerification = async () => {
    if (checkingEmailVerification) return;
    
    setCheckingEmailVerification(true);
    setError(null);
    
    try {
      const isVerified = await checkEmailVerified(user);
      if (isVerified) {
        setStep(1); // Move to phone setup step
        console.log("Email verified! Moving to phone setup step");
      } else {
        setError("Email not yet verified. Please check your email and click the verification link.");
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
      setError('Error checking email verification status. Please try again.');
    } finally {
      setCheckingEmailVerification(false);
    }
  };

  // Clean up email check interval on unmount
  useEffect(() => {
    return () => {
      if (emailCheckInterval) {
        clearInterval(emailCheckInterval);
      }
    };
  }, [emailCheckInterval]);

  // Handle fallback option (when users can't use phone auth)
  const handleFallbackOption = () => {
    // Clean up any lingering recaptcha verifiers
    try {
      clearRecaptchaVerifier();
    } catch (err) {
      console.warn("Error cleaning up reCAPTCHA:", err);
    }
    
    // Show a helpful message
    alert(`
      Please try these troubleshooting steps:

      1. Ensure your browser allows third-party cookies
      2. Try using a different browser
      3. Disable any ad blockers or privacy extensions
      4. Disable VPN if you're using one
      5. Contact support if problems persist

      You will now be redirected to the main page.
    `);
    
    // Close the enrollment flow
    onCancel();
  };

  // Email verification screen - Step 0
  const renderEmailVerificationStep = () => {
    return (
      <div>
        <div className="mb-5">
          <h2 className="text-xl font-semibold mb-2">Verify Your Email</h2>
          <p className="text-gray-600 dark:text-white/70">
            Before setting up two-factor authentication, you need to verify your email address:{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              {user?.email}
            </span>
          </p>
        </div>

        {emailVerificationSent && (
          <div className="mb-5 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500/30 rounded-lg text-sm">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="font-medium text-blue-800 dark:text-blue-400">Verification email sent!</p>
                <p className="text-blue-700 dark:text-blue-300 mt-1">
                  Please check your email and click the verification link. 
                  {checkingEmailVerification && (
                    <span className="ml-2 text-sm">
                      <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-blue-600 dark:text-blue-400 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking verification status...
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {!emailVerificationSent ? (
            <button
              type="button"
              onClick={handleSendEmailVerification}
              className="w-full px-4 py-3 rounded-lg bg-brand hover:bg-[#F9A06B] text-black font-medium transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Verification Email'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCheckEmailVerification}
              className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50"
              disabled={checkingEmailVerification}
            >
              {checkingEmailVerification ? 'Checking...' : 'I\'ve Verified My Email'}
            </button>
          )}

          {emailVerificationSent && !checkingEmailVerification && (
            <button
              type="button"
              onClick={handleSendEmailVerification}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>
          )}

          <button
            type="button"
            onClick={onCancel}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Verification code screen - Step 2
  const renderVerificationStep = () => {
    return (
      <div>
        <div className="mb-5">
          <h2 className="text-xl font-semibold mb-2">Enter verification code</h2>
          <p className="text-gray-600 dark:text-white/70">
            We've sent a 6-digit verification code to{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              {phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '')}
              {/* Add fallback if phoneNumber is just the country code */}
              {phoneNumber === '+1' && ' (Number not displayed fully)'}
            </span>.
          </p>
          
          {/* Debug info for troubleshooting */}
          {TEST_MODE && (
            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-md">
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                <span className="font-semibold">Debug Info:</span> 
                {verificationId ? ` Verification ID received` : ' No verification ID'}
                {verificationId?.startsWith('mock-verification-id-') && 
                  ` (Test Mode - Use code: 123456)`
                }
              </p>
            </div>
          )}
          
          {TEST_MODE && verificationId?.startsWith('mock-verification-id-') && (
            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-md">
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                <span className="font-semibold">Test Mode:</span> Use verification code <code className="font-mono bg-blue-100 dark:bg-blue-800/50 px-1.5 py-0.5 rounded">123456</code>
              </p>
            </div>
          )}
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🔍 DEBUG: Verification form onSubmit triggered');
            handleVerifyCode(e);
            return false;
          }}
          noValidate
        >
          <div className="mb-4">
            <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1">
              Verification Code
            </label>
            <input
              type="text"
              id="verification-code"
              ref={verificationInputRef}
              className="w-full text-lg px-3 py-2 border border-gray-300 dark:border-white/20 dark:bg-white/5 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
            />
          </div>
          
          {error && (
            <div className="mb-4 p-3 text-sm rounded-md bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-100 dark:border-red-800/50">
              {error}
            </div>
          )}

          <div className="mb-4">
            <button 
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 DEBUG: Verify Code button clicked');
                handleVerifyCode(e);
              }}
              className={`w-full px-4 py-2 text-white font-medium rounded-md ${loading ? 'bg-gray-400 dark:bg-gray-700 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors`}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </div>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300"
              onClick={handleResendCode}
              disabled={loading}
            >
              Didn't receive the code? Send again
            </button>
          </div>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => {
                console.log("User manually returning to step 1");
                setStep(1);
                setError(null);
                setVerificationId('');
                setVerificationCode('');
              }}
              disabled={loading}
            >
              ← Back to phone number
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="relative p-4 sm:p-6 bg-white dark:bg-black/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 dark:border-white/10 w-full max-w-md mx-auto">
      {/* Hidden recaptcha container */}
      <div id="recaptcha-container" ref={recaptchaContainerRef} style={{ display: 'none' }}></div>
      
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Set Up Two-Factor Authentication
      </h2>
      
      {/* Reauthentication overlay - shown on top of current step instead of replacing it */}
      {needsReauth && (
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl z-10"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('🔍 DEBUG: Overlay background clicked - preventing all propagation');
          }}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('🔍 DEBUG: Overlay submit event intercepted and stopped');
          }}
        >
          <div 
            className="bg-white dark:bg-black/95 p-6 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 max-w-sm w-full mx-4"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              console.log('🔍 DEBUG: ReauthDialog container clicked - preventing propagation');
            }}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              console.log('🔍 DEBUG: ReauthDialog container submit event intercepted');
            }}
          >
            <ReauthDialog
              onSuccess={handleReauthSuccess}
              onCancel={() => {
                console.log('🔍 DEBUG: ReauthDialog onCancel called');
                setNeedsReauth(false);
                // Don't call onCancel here - just close the reauth dialog
              }}
            />
          </div>
        </div>
      )}
      
      {/* Main content - always visible but dimmed when reauthentication is needed */}
      <div className={needsReauth ? 'opacity-50 pointer-events-none' : ''}>
        {step === 1 ? (
          <>
            <p className="text-sm text-gray-700 dark:text-white/80 mb-4 sm:mb-6">
              Add an extra layer of security to your account by enabling two-factor authentication.
            </p>

            <form 
              ref={formRef} 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 DEBUG: Form onSubmit triggered');
                handleSendCode(e);
                return false;
              }}
              noValidate
            >
              <div className="mb-5">
                <label 
                  htmlFor="phone-number" 
                  className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1"
                >
                  Phone Number
                </label>
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
                  We'll send a verification code to this number when you sign in
                </p>
              </div>

              {error && (
                <div className="mb-5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg text-sm text-red-600 dark:text-red-400">
                  {typeof error === 'string' ? error : error}
                </div>
              )}

              {recaptchaFailed && (
                <div className="mb-5 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-400">
                    Having trouble?
                  </p>
                  <p className="mt-1 text-yellow-700 dark:text-yellow-300">
                    If you're unable to proceed with phone verification, you can try again later or contact support.
                  </p>
                  <button
                    type="button"
                    onClick={handleFallbackOption}
                    className="mt-2 text-sm text-yellow-800 dark:text-yellow-400 underline hover:no-underline"
                  >
                    Contact support for alternative options
                  </button>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔍 DEBUG: Cancel button clicked');
                    onCancel();
                  }}
                  className="mt-2 sm:mt-0 w-full sm:w-auto px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 text-gray-700 dark:text-white/80 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔍 DEBUG: Send Verification Code button clicked');
                    handleSendCode(e);
                  }}
                  className="w-full sm:w-auto px-4 py-3 rounded-lg bg-brand hover:bg-[#F9A06B] text-black font-medium transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            </form>
          </>
        ) : step === 2 ? (
          renderVerificationStep()
        ) : (
          renderEmailVerificationStep()
        )}
      </div>
    </div>
  );
};

export default MfaEnrollment; 